// キー入力管理。
// 設計のキモ:
//  - "hold" (押しっぱなし) と "pressed" (今フレーム押された) を区別する。
//  - "pressed" は入力バッファに積まれ、最大 N フレーム保持される。
//    これにより「攻撃終わり際に押したパリィ」が次フレームで拾われ、
//    "押したのに反応しなかった" 感覚を撲滅する。

import { ARENA, PLAYER } from './balance'
import { setLabelBindings, type InputDeviceView } from './input_labels'
import type { MenuRect } from './menu_layout'
import {
  assignKbm, assignPad, cloneBindings, DEFAULT_KEY_BINDINGS, isAssignableKbmCode, isAssignablePadButton,
  KBM_BIND_ACTIONS, loadKeyBindings, PAD_BIND_ACTIONS, saveKeyBindings,
  type KbmBindAction, type KeyBindings, type PadBindAction,
} from './key_bindings'

export type InputAction = 'attack' | 'parry' | 'step' | 'retry' | 'reset' | 'pause'

export type Input = {
  moveX: number               // -1 / 0 / 1
  // -1 (上) / 0 / 1 (下)。 縦メニュー(タイトル等)のナビ用。 省略可 (テスト/bot は未指定=0扱い)。
  moveY?: number
  hold: Record<InputAction, boolean>
  // バッファ済みプレス。consume() で取り出すと消える。
  pressed: ReadonlySet<InputAction>
  // アクティブだったパッドが今フレーム切断された (エッジ・1フレームだけ true)。省略可 = 既存テスト/bot は無変更で通る。
  padDisconnected?: boolean
  // 最後に入力があったデバイス種別 (lastDeviceKind)。省略可 = 既存テスト/bot は無変更で通る。
  device?: 'kbm' | 'pad'
  // 各アクションの連続押下時間 (ms)。省略可 = 既存テスト/bot は無変更で通る。
  holdMs?: Partial<Record<InputAction, number>>
  // この tick で縦方向の入力が「押された瞬間」を観測したか (-1=上 / 0=なし / 1=下)。
  //   縦メニュー専用。 keydown と keyup が同じ tick の隙間に収まっても取りこぼさないために、
  //   hold ではなくイベント側で拾って readInput() で回収する。
  //   省略可 = 既存テスト/bot は未指定 (0扱い) で通る。
  navEdgeY?: -1 | 0 | 1
  // 同じく横方向 (-1=左 / 0=なし / 1=右)。 横並びの選択肢専用。
  navEdgeX?: -1 | 0 | 1
  // マウスカーソルの位置 (ARENA 960x540 座標)。 マウスが無い/未移動なら undefined。
  //   moved = 直近の tick で実際に動いたか (動いていない時にホバーでカーソルを奪わないため)。
  pointer?: { x: number, y: number, moved: boolean }
  // 左クリックが押された瞬間の位置 (ARENA 座標)。 pressed('attack') と同じ tick で読む。
  //   ⚠️ 押した瞬間に確定する = 読む時点のマウス位置ではない (押してから動かしても対象が変わらない)。
  pointerPress?: { x: number, y: number }
  // マウスのホイール (この tick に回した量・下=正・行の数に丸めた物)。 クレジットの送り専用 (2026-10-31)。 省略=0。
  wheelY?: number
  // 幕間ADVの選択肢が出ている時だけ、 main.ts が毎フレーム計測して載せる当たり矩形。
  //   render.ts の advChoiceOptionRects と同じ出所 (2026-07-25 第4段)。 省略可 = 既存テスト/bot は
  //   未指定 (選択肢のマウス判定なし) で通る。 ⚠️ モジュールスコープの可変変数に置かない
  //   (types.ts の設計原則)。 Input は「外の世界→ロジック」の既存の通り道。
  advChoiceRects?: MenuRect[]
}

type InputBufferEntry = {
  action: InputAction
  ageFrames: number
}

// 2026-07-26 ユーザー裁定・他ゲーム標準へ整理: kbm は「移動=A/D」「メニュー操作はマウス専用」
//   「物語画面だけ Space でも進める」に統一する。 重複キー (旧ホームポジション最適化 J/K/L/X、
//   矢印キー移動、W/S/矢印での縦メニュー、Shift回避、R/Enterでの決定) は全て撤去した。
//   プレイヤーが進めるつもりが無いのに触れて画面が進む事故を無くすのが理由。
// 残すキー: A/D=移動 / 左クリック=攻撃・メニュー決定・文章送り / 右クリック=回避 /
//   Space=パリィ・文章送り(物語画面のみ) / Escape=幕間 / T=開発限定の進捗リセット。
type KeyMapped = InputAction | 'left' | 'right' | 'up' | 'down'
// 割り当てから引く表を作る。 戦闘の5つの所作は key_bindings.ts の割り当て (既定は上の通り) から、
//   Escape=幕間 と T=開発限定の進捗リセット は固定。
function buildKeyMap(b: KeyBindings): Record<string, KeyMapped> {
  const m: Record<string, KeyMapped> = {}
  for (const a of KBM_BIND_ACTIONS) m[b.kbm[a]] = a
  // 進捗リセット (T) は開発中だけ。 配布版では割り当てない = 配る側で全消去の事故を起こさない
  //   (2026-07-26 ユーザー裁定。 R (再演) の隣で、 画面に書いてもいない全消去キーだった)。
  if (import.meta.env.DEV && !m.KeyT) m.KeyT = 'reset'
  m.Escape = 'pause' // 幕間メニューを開く / 閉じる
  return m
}
// 既定の表 = メニューと物語画面で使う (左クリック=決定・文章送り / Space=文章送り / 右クリック / A・D)。
const UI_KEY_MAP = buildKeyMap(DEFAULT_KEY_BINDINGS)

// ゲームパッド (標準マッピング前提):
//   SEKIRO / Lies of P 準拠へ変更 (2026-07-23・出所=docs/candidates/2026-07-23-controller-default-mapping-sekiro-liesofp-research.md)。
//   両作品で完全一致した core3 (攻撃=R1 / 弾き=L1 / 回避=○) をそのまま踏襲。
//   PS5表記との対応: 5=R1, 4=L1, 1=○(East/Circle), 0=✕(South/Cross), 9=Options。
//   進捗リセット(reset)はパッドに載せない — 誤爆で localStorage が飛ぶため、開発用の Key T のみに残す。
//   戦闘中の攻撃・パリィ・回避は key_bindings.ts の割り当て (既定 5=R1 攻撃 / 4=L1 弾き・長押しで受け / 1=○ 回避)。
//   0=✕ はリトライ (PS「決定=✕」慣習)、 9=Options は戦闘中=幕間メニュー / それ以外=進む (game.ts で分岐)。
function buildPadMap(b: KeyBindings): Record<number, InputAction> {
  const m: Record<number, InputAction> = { 0: 'retry', 9: 'pause' }
  for (const a of PAD_BIND_ACTIONS) m[b.pad[a]] = a
  return m
}
const UI_PAD_MAP = buildPadMap(DEFAULT_KEY_BINDINGS)

// ===== 割り当ての今 (2026-10-31) =====
//   戦闘中 (uiMode=false) だけ作者の割り当てを使う。 メニュー・物語・幕間 (uiMode=true) は既定の表のまま
//   = どう割り当てても、 メニューの決定・戻る・文章送りは変わらない。 main.ts が毎 tick setInputUiMode を呼ぶ。
let bindings: KeyBindings = loadKeyBindings()
let playKeyMap = buildKeyMap(bindings)
let playPadMap = buildPadMap(bindings)
setLabelBindings(bindings)
let uiMode = true

export function getKeyBindings(): KeyBindings { return cloneBindings(bindings) }
export function setKeyBindings(next: KeyBindings, persist = true): void {
  bindings = cloneBindings(next)
  playKeyMap = buildKeyMap(bindings)
  playPadMap = buildPadMap(bindings)
  setLabelBindings(bindings)
  if (persist) saveKeyBindings(bindings)
}
export function setInputUiMode(on: boolean): void { uiMode = on }

function keyMap(): Record<string, KeyMapped> { return uiMode ? UI_KEY_MAP : playKeyMap }
function padMap(): Record<number, InputAction> { return uiMode ? UI_PAD_MAP : playPadMap }

// 押した瞬間の動作。 戦闘中に ✕ を攻撃などへ割り当てた時も、 ✕ はリトライを兼ねる
//   (戦闘中の TIPS のカードはパッドなら ✕ で閉じる = game.ts の confirmPressed)。
function padActionsFor(button: number): InputAction[] {
  const a = padMap()[button]
  const out: InputAction[] = a ? [a] : []
  if (button === 0 && a !== 'retry') out.push('retry')
  return out
}

// ===== 割り当ての受付 (設定画面の「次に押したキーを割り当てる」) =====
export type KeyCapture = { kind: 'kbm', action: KbmBindAction } | { kind: 'pad', action: PadBindAction }
let capture: KeyCapture | null = null
// 受付で使ったキー。 離すまではゲーム入力として拾わない (割り当てた瞬間に攻撃が出ない)。
const captureSwallowed = new Set<string>()
export function beginKeyCapture(c: KeyCapture): void {
  capture = c
}
export function cancelKeyCapture(): void { capture = null }
export function keyCaptureView(): KeyCapture | null { return capture }

// 純関数 (テスト対象): 前フレームとの差分から「今押された」アクションを返す。
export function mapGamepadEdges(prev: ReadonlySet<number>, curr: ReadonlySet<number>): InputAction[] {
  const out: InputAction[] = []
  for (const b of curr) {
    if (prev.has(b)) continue
    for (const a of padActionsFor(b)) if (!out.includes(a)) out.push(a)
  }
  return out
}

// 純関数 (テスト対象): 現在押下中のボタン集合 → hold 扱いにするアクション集合。
//   キーボードの heldKeys と対称にするため、割当のある全アクションを対象にする。
export function mapGamepadHeld(curr: ReadonlySet<number>): Set<InputAction> {
  const out = new Set<InputAction>()
  for (const b of curr) {
    for (const a of padActionsFor(b)) out.add(a)
  }
  return out
}

// 純関数 (テスト対象): スティックx + 十字キーから移動軸 -1/0/1。デッドゾーン 0.35。
export function gamepadMoveAxis(axisX: number, dpadLeft: boolean, dpadRight: boolean): number {
  if (dpadLeft) return -1
  if (dpadRight) return 1
  if (axisX <= -0.35) return -1
  if (axisX >= 0.35) return 1
  return 0
}

export type PadSnapshot = { connected: boolean, hasInput: boolean } | null

// 純関数 (テスト対象): アクティブに使うパッドの index を決める。
//   入力のあるパッドを優先 (現アクティブが入力中なら維持)。無ければ現アクティブを維持、
//   それも切断済みなら最初の接続パッド。1つも無ければ null。
export function pickActivePadIndex(pads: ReadonlyArray<PadSnapshot>, current: number | null): number | null {
  if (current !== null && pads[current]?.connected && pads[current]?.hasInput) return current
  for (let i = 0; i < pads.length; i += 1) {
    if (pads[i]?.connected && pads[i]?.hasInput) return i
  }
  if (current !== null && pads[current]?.connected) return current
  for (let i = 0; i < pads.length; i += 1) {
    if (pads[i]?.connected) return i
  }
  return null
}

function padHasInput(p: Gamepad): boolean {
  if (p.buttons.some((b) => b.pressed)) return true
  const ax0 = p.axes[0] ?? 0
  const ax1 = p.axes[1] ?? 0
  return Math.abs(ax0) >= 0.35 || Math.abs(ax1) >= 0.35
}

let padPrevButtons: ReadonlySet<number> = new Set()
let padMoveX = 0
let padMoveY = 0
let padHeldActions: ReadonlySet<InputAction> = new Set()
let padRearm = false
let padActiveIndex: number | null = null
let padLostEdge = false
// 最後に入力があったデバイス種別。表記の動的切替 (P4) が読む。
let lastDeviceKind: 'kbm' | 'pad' = 'kbm'
let padGlyph: 'ps' | 'xbox' = 'ps'

// フォーカス喪失/切断でパッド状態を白紙化。復帰後の最初のポーリングは
//   「その時押されていたボタン」を前状態として飲み込み、エッジを発火させない
//   (Alt+Tab 中に押しっぱなしだった L1 が、復帰の瞬間に偽パリィになるのを防ぐ)。
// ⚠️ navEdgePending はここでは触らない: pollGamepad() はパッド未接続 (keyboard-only 環境)
//   の間 毎tick これを呼ぶため、ここで navEdgePending=0 すると keydown で立てた直後のエッジを
//   次の readInput() が読む前に握り潰してしまう (2026-07-25 実測で発覚したレース)。
//   navEdgePending の明示クリアは blur / visibilitychange 側で個別に行う。
function wipePadState(): void {
  padPrevButtons = new Set()
  padMoveX = 0; padMoveY = 0
  padHeldActions = new Set()
  padRearm = true
}

// 毎tick呼ぶ。「入力があったパッド」をアクティブとして選び、押下エッジをバッファへ積む。
function pollGamepad(): void {
  if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return
  const raw = navigator.getGamepads()
  const list: (Gamepad | null)[] = raw ? Array.from(raw) : []
  const snaps: PadSnapshot[] = list.map((p) => (p ? { connected: !!p.connected, hasInput: padHasInput(p) } : null))
  const prevActiveIndex = padActiveIndex
  const nextIndex = pickActivePadIndex(snaps, padActiveIndex)

  if (nextIndex === null) {
    if (prevActiveIndex !== null && lastDeviceKind === 'pad') padLostEdge = true
    padActiveIndex = null
    wipePadState()
    return
  }

  // 別パッドへの乗り換えだけ rearm する。null→初回接続は padPrevButtons が既に空なので
  //   そのままエッジ判定してよい (むしろここで飲み込むと、パッドを可視化させた最初の一押しが消える)。
  //   なお切断→再接続は wipePadState() の rearm が残っているため、起こしの一押しは飲み込まれる
  //   (戦闘中の BT 復帰でいきなりパリィが出ない = 意図した安全側の非対称)。
  if (prevActiveIndex !== null && nextIndex !== prevActiveIndex) padRearm = true
  padActiveIndex = nextIndex

  const gp = list[nextIndex]!
  const curr = new Set<number>()
  gp.buttons.forEach((b, i) => { if (b.pressed) curr.add(i) })

  if (capture && !padRearm) {
    // 受付中: 新しく押したボタンを割り当てる (OPTIONS=やめる)。 このボタンはゲーム入力に流さない。
    for (const b of curr) {
      if (padPrevButtons.has(b)) continue
      if (b === 9) { capture = null; break }
      if (capture.kind === 'pad' && isAssignablePadButton(b)) {
        setKeyBindings(assignPad(bindings, capture.action, b))
        capture = null
        break
      }
    }
    padPrevButtons = curr
    padHeldActions = new Set()
    lastDeviceKind = curr.size > 0 ? 'pad' : lastDeviceKind
    return
  }
  const edges = padRearm ? [] : mapGamepadEdges(padPrevButtons, curr)
  for (const a of edges) buffer.push({ action: a, ageFrames: 0 })

  padHeldActions = mapGamepadHeld(curr)
  padMoveX = gamepadMoveAxis(gp.axes[0] ?? 0, curr.has(14), curr.has(15))
  padMoveY = gamepadMoveAxis(gp.axes[1] ?? 0, curr.has(12), curr.has(13))  // 上(12)/下(13) 十字 + 左スティックY
  padPrevButtons = curr
  // Chrome の id は "Wireless Controller (STANDARD GAMEPAD Vendor: 054c Product: 0ce6)" 形式。
  //   VID での判別が製品名より堅い (docs/candidates/2026-07-24-controller-full-support-ticket.md §8)。
  padGlyph = gp.id.toLowerCase().includes('054c') ? 'ps' : 'xbox'
  if (edges.length > 0 || padMoveX !== 0 || padMoveY !== 0 || padHeldActions.size > 0) lastDeviceKind = 'pad'
  padRearm = false
}

const heldKeys = new Set<string>()
const buffer: InputBufferEntry[] = []
// 次の readInput() で回収する方向エッジ (軸ごと)。 同 tick に複数来たら最後の1つを採る。
let navEdgeYPending: -1 | 0 | 1 = 0
let navEdgeXPending: -1 | 0 | 1 = 0

// マウスカーソル (ARENA 960x540 座標)。 canvas 要素の実サイズから単純比例で変換する
//   (index.html の #stage はレターボックス無しで canvas を等比拡大しているため)。
let inputCanvas: HTMLCanvasElement | null = null
let pointerX = 0
let pointerY = 0
let pointerMovedSinceRead = false
let pointerPressPending: { x: number, y: number } | null = null
let wheelPending = 0
function toArenaCoords(clientX: number, clientY: number): { x: number, y: number } {
  const r = inputCanvas!.getBoundingClientRect()
  return {
    x: (clientX - r.left) / r.width * ARENA.width,
    y: (clientY - r.top) / r.height * ARENA.height,
  }
}

// 各アクションの連続押下時間 (ms)。readInput() で hold の結果を見て毎tick更新する。
const holdMsMap: Record<InputAction, number> = {
  attack: 0,
  parry: 0,
  step: 0,
  retry: 0,
  reset: 0,
  pause: 0,
}

let initialized = false

// 文字を打っている最中 (感想入力欄など) はゲーム入力として拾わない。
//   これが無いと ①打鍵がそのまま攻撃/進行になり ②mapped キーは preventDefault されて
//   そもそも文字が入らない。 DOM の入力欄を画面に出すなら必ず要る門番。
function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  if (!el || !el.tagName) return false
  const tag = el.tagName.toLowerCase()
  return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable === true
}

export function initInput(canvasEl?: HTMLCanvasElement): void {
  if (canvasEl) inputCanvas = canvasEl
  if (initialized) return
  initialized = true

  window.addEventListener('keydown', (e) => {
    if (isTypingTarget(e.target)) return
    if (capture) {
      e.preventDefault()
      if (e.repeat || captureSwallowed.has(e.code)) return
      captureSwallowed.add(e.code)
      lastDeviceKind = 'kbm'
      if (e.code === 'Escape') { capture = null; return }
      if (capture.kind === 'kbm' && isAssignableKbmCode(e.code)) {
        setKeyBindings(assignKbm(bindings, capture.action, e.code))
        capture = null
      }
      return
    }
    if (captureSwallowed.has(e.code)) { e.preventDefault(); return }
    const mapped = keyMap()[e.code]
    if (!mapped) return
    lastDeviceKind = 'kbm'
    // ブラウザ既定動作の抑止（スペースでスクロールなど）
    if (mapped !== 'left' && mapped !== 'right') e.preventDefault()

    if (heldKeys.has(e.code)) return  // リピート発火を無視
    heldKeys.add(e.code)

    if (mapped === 'up') navEdgeYPending = -1
    else if (mapped === 'down') navEdgeYPending = 1
    else if (mapped === 'left') navEdgeXPending = -1
    else if (mapped === 'right') navEdgeXPending = 1

    // 移動軸 (left/right/up/down) はバッファに積まない (= hold/axis で読む)。 アクションのみ積む。
    if (mapped !== 'left' && mapped !== 'right' && mapped !== 'up' && mapped !== 'down') {
      buffer.push({ action: mapped, ageFrames: 0 })
    }
  })

  window.addEventListener('keyup', (e) => {
    heldKeys.delete(e.code)
    captureSwallowed.delete(e.code)
  })

  // マウス移動: メニューのホバー選択専用の位置を保持する (canvas 未配線なら無効のまま)。
  window.addEventListener('mousemove', (e) => {
    if (!inputCanvas) return
    const p = toArenaCoords(e.clientX, e.clientY)
    pointerX = p.x; pointerY = p.y
    pointerMovedSinceRead = true
  })

  // ホイール: クレジットの長い文を送るだけに使う (戦闘・メニューの選択には使わない)。
  window.addEventListener('wheel', (e) => {
    if (!inputCanvas) return
    wheelPending += e.deltaY
  }, { passive: true })

  // マウス: 左/右クリックを synthetic code "Mouse<button>" としてキー機構に相乗りさせる。
  window.addEventListener('mousedown', (e) => {
    if (isTypingTarget(e.target)) return   // 入力欄のクリック = カーソル移動であって攻撃ではない
    if (capture) {
      e.preventDefault()
      const code = `Mouse${e.button}`
      captureSwallowed.add(code)
      lastDeviceKind = 'kbm'
      if (capture.kind === 'kbm' && isAssignableKbmCode(code)) {
        setKeyBindings(assignKbm(bindings, capture.action, code))
        capture = null
      }
      return
    }
    // 左クリックが押された「その瞬間」の位置。 メニューの位置クリック判定専用 (第2段)。
    if (e.button === 0 && inputCanvas) {
      pointerPressPending = toArenaCoords(e.clientX, e.clientY)
    }
    const code = `Mouse${e.button}`
    if (captureSwallowed.has(code)) return
    const mapped = keyMap()[code]
    if (!mapped) return
    lastDeviceKind = 'kbm'
    e.preventDefault()  // テキスト選択・中ボタンスクロール等の抑止
    if (heldKeys.has(code)) return
    heldKeys.add(code)
    // 移動 (左右) をマウスへ割り当てた時は hold で読む。 バッファに積むのは所作だけ。
    if (mapped === 'left' || mapped === 'right' || mapped === 'up' || mapped === 'down') return
    buffer.push({ action: mapped, ageFrames: 0 })
  })

  window.addEventListener('mouseup', (e) => {
    heldKeys.delete(`Mouse${e.button}`)
    captureSwallowed.delete(`Mouse${e.button}`)
  })

  // 右クリックのブラウザ既定メニューを抑止（回避にあてるため）
  window.addEventListener('contextmenu', (e) => {
    e.preventDefault()
  })

  window.addEventListener('blur', () => {
    heldKeys.clear()
    captureSwallowed.clear()
    buffer.length = 0
    navEdgeYPending = 0
    wheelPending = 0
    navEdgeXPending = 0
    pointerPressPending = null   // pointer (位置) は保持でよい。 押下の「瞬間」だけ破棄する。
    wipePadState()
  })

  // タブ非フォーカス→復帰でもパッド状態を白紙化 (blur が発火しないブラウザ/OS の遷移を補う)。
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) { wipePadState(); navEdgeYPending = 0; navEdgeXPending = 0; pointerPressPending = null }
    })
  }
}

function holdState(): Record<InputAction, boolean> {
  const result: Record<InputAction, boolean> = {
    attack: false,
    parry: false,
    step: false,
    retry: false,
    reset: false,
    pause: false,
  }
  const map = keyMap()
  for (const code of heldKeys) {
    const action = map[code]
    if (action && action !== 'left' && action !== 'right' && action !== 'up' && action !== 'down') {
      result[action] = true
    }
  }
  for (const a of padHeldActions) result[a] = true
  return result
}

// 現在の入力表記デバイス (最後に入力があった側)。render がバッジ/掲示文言の解決に毎フレーム読む。
export function inputDeviceView(): InputDeviceView {
  return { device: lastDeviceKind, padGlyph }
}

function moveAxis(): number {
  let axis = 0
  const kbm = uiMode ? DEFAULT_KEY_BINDINGS.kbm : bindings.kbm
  if (heldKeys.has(kbm.left)) axis -= 1
  if (heldKeys.has(kbm.right)) axis += 1
  // キー入力が無いときはゲームパッドの軸を採用
  if (axis === 0) axis = padMoveX
  return axis
}

// 縦軸 (上=-1 / 下=+1)。 縦メニュー(タイトル)専用。 ゲーム本編は上下移動を持たないので使わない。
//   2026-07-26 ユーザー裁定でメニューはマウス専用へ整理したため、 kbm 側のキー割り当ては無い
//   (この関数は padMoveY だけを返す＝パッドの十字/左スティックの縦ナビは維持)。
function moveYAxis(): number {
  return padMoveY
}

// 押しのバッファの寿命を止めるか (DEV の比較版だけが立てる・既定 false = 毎 readInput() で寿命が進む)。
//   ヒットストップ中はゲームの時間と判定が止まるのに readInput() だけは毎更新呼ばれ、押しが
//   「ストップ明けまで持たない」。 main.ts が DEV の比較版の時だけ、ストップ中の更新で true にする
//   (docs/research/2026-10-09-hitstop-input-buffer-status.md)。
//   止めるのは寿命 (ageFrames の加算と期限切れの破棄) だけ。 新しい押し・hold・移動軸・メニューのエッジは普段どおり。
let bufferAgingFrozen = false
export function setInputBufferAgingFrozen(frozen: boolean): void { bufferAgingFrozen = frozen }

// 1tickごとに呼ぶ。バッファをエージングし、寿命切れを破棄して新しいスナップショットを返す。
export function readInput(): Input {
  pollGamepad()
  if (!bufferAgingFrozen) {
    for (const entry of buffer) entry.ageFrames += 1
    while (buffer.length > 0 && buffer[0]!.ageFrames > PLAYER.inputBufferFrames) {
      buffer.shift()
    }
  }

  const pressed = new Set<InputAction>(buffer.map((b) => b.action))

  const padDisconnected = padLostEdge
  padLostEdge = false

  const hold = holdState()
  for (const action of Object.keys(holdMsMap) as InputAction[]) {
    holdMsMap[action] = hold[action] ? holdMsMap[action] + 1000 / 60 : 0
  }

  const navEdgeY = navEdgeYPending
  const navEdgeX = navEdgeXPending
  navEdgeYPending = 0
  navEdgeXPending = 0

  const pointer = inputCanvas ? { x: pointerX, y: pointerY, moved: pointerMovedSinceRead } : undefined
  pointerMovedSinceRead = false
  const pointerPress = pointerPressPending ?? undefined
  pointerPressPending = null
  const wheelY = Math.trunc(wheelPending / 40)
  wheelPending -= wheelY * 40

  return {
    moveX: moveAxis(),
    moveY: moveYAxis(),
    hold,
    pressed,
    padDisconnected,
    device: lastDeviceKind,
    holdMs: { ...holdMsMap },
    navEdgeY,
    navEdgeX,
    pointer,
    pointerPress,
    wheelY,
  }
}

// バッファにまだ残っている押しがあるか (読むだけ・寿命も進めない)。main.ts が致命を弾いた後のゆっくりの間を
//   次の更新を待たずに切るために使う (1014c・src/exec_parry_slow_fx.ts の execSlowCutNow)。
export function hasBufferedAction(actions: readonly InputAction[]): boolean {
  return buffer.some((b) => actions.includes(b.action))
}

// アクションを「消費」した側がここを呼ぶことでバッファから抜く。
// これにより同じプレスが二度発火しない。
export function consume(action: InputAction): void {
  for (let i = 0; i < buffer.length; i += 1) {
    if (buffer[i]!.action === action) {
      buffer.splice(i, 1)
      return
    }
  }
}

// メニュー/パネルを開いた瞬間など、 直前の方向入力を持ち込ませたくない時に呼ぶ。
export function consumeNav(): void { navEdgeYPending = 0; navEdgeXPending = 0 }

// テスト用ヘルパ
export function _resetInputForTest(): void {
  heldKeys.clear()
  buffer.length = 0
  bufferAgingFrozen = false
  padPrevButtons = new Set()
  padMoveX = 0; padMoveY = 0
  padHeldActions = new Set()
  padRearm = false
  padActiveIndex = null
  padLostEdge = false
  lastDeviceKind = 'kbm'
  padGlyph = 'ps'
  navEdgeYPending = 0
  navEdgeXPending = 0
  pointerX = 0; pointerY = 0
  pointerMovedSinceRead = false
  pointerPressPending = null
  wheelPending = 0
  capture = null
  captureSwallowed.clear()
  uiMode = true
  setKeyBindings(DEFAULT_KEY_BINDINGS, false)
  for (const action of Object.keys(holdMsMap) as InputAction[]) holdMsMap[action] = 0
}
